import * as THREE from 'three';
import type { GridGeometry } from '../../grid';
import type { MapDefinition, TileData } from '../../model/gamestate/GameState';

const HEX_RADIUS = 0.75;
const SQUARE_HALF = 0.75;
const TRIANGLE_HALF = 0.75;
const TRIANGLE_HALF_HEIGHT = TRIANGLE_HALF * Math.sqrt(3) / 2;

export interface TileWorldData {
  center: THREE.Vector3;
  topY: number;
}

export function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

export function getTileTopHeight(tile: TileData): number {
  void tile;
  return 0.02;
}

export function mapPixelCenter(map: MapDefinition, grid: GridGeometry, tileSize: number): THREE.Vector2 {
  const dims = grid.mapPixelDimensions(map.width, map.height, tileSize);
  return new THREE.Vector2(dims.w / (tileSize * 2), dims.h / (tileSize * 2));
}

export function tileWorldCenter(grid: GridGeometry, tileSize: number, x: number, y: number): THREE.Vector3 {
  const pixel = grid.tileToPixel(x, y, tileSize);
  const centroid = grid.tileCentroid(tileSize, x, y);
  return new THREE.Vector3(
    (pixel.x + centroid.x) / tileSize,
    0,
    (pixel.y + centroid.y) / tileSize,
  );
}

export function createTileShape(gridType: string, tile: TileData): THREE.Shape {
  switch (gridType) {
    case 'square':
      return createSquareShape();
    case 'triangle':
      return createTriangleShape(tile.x, tile.y);
    case 'hex':
    default:
      return createHexShape();
  }
}

export function createTileGeometry(gridType: string, tile: TileData): THREE.ExtrudeGeometry {
  const shape = createTileShape(gridType, tile);
  const height = getTileTopHeight(tile);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: height,
    bevelEnabled: false,
    curveSegments: gridType === 'hex' ? 6 : 1,
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.computeVertexNormals();
  return geometry;
}

export function createTileTopGeometry(gridType: string, tile: TileData): THREE.ShapeGeometry {
  const geometry = new THREE.ShapeGeometry(createTileShape(gridType, tile));
  geometry.rotateX(-Math.PI / 2);

  const uvAttribute = geometry.getAttribute('uv');
  if (uvAttribute) {
    for (let index = 0; index < uvAttribute.count; index += 1) {
      const u = uvAttribute.getX(index);
      const v = uvAttribute.getY(index);
      uvAttribute.setXY(index, u * (2 / 3) + (1 / 6), v * (2 / 3) + (1 / 6));
    }
    uvAttribute.needsUpdate = true;
  }

  return geometry;
}

function createHexShape(): THREE.Shape {
  const shape = new THREE.Shape();
  for (let index = 0; index < 6; index += 1) {
    const angle = (Math.PI / 3) * index;
    const px = Math.cos(angle) * HEX_RADIUS;
    const py = Math.sin(angle) * HEX_RADIUS;
    if (index === 0) {
      shape.moveTo(px, py);
    } else {
      shape.lineTo(px, py);
    }
  }
  shape.closePath();
  return shape;
}

function createSquareShape(): THREE.Shape {
  const shape = new THREE.Shape();
  shape.moveTo(-SQUARE_HALF, -SQUARE_HALF);
  shape.lineTo(SQUARE_HALF, -SQUARE_HALF);
  shape.lineTo(SQUARE_HALF, SQUARE_HALF);
  shape.lineTo(-SQUARE_HALF, SQUARE_HALF);
  shape.closePath();
  return shape;
}

function createTriangleShape(x: number, y: number): THREE.Shape {
  const shape = new THREE.Shape();
  const pointsUp = ((x + y) & 1) === 0;
  if (pointsUp) {
    shape.moveTo(0, -TRIANGLE_HALF_HEIGHT * (2 / 3));
    shape.lineTo(TRIANGLE_HALF, TRIANGLE_HALF_HEIGHT / 3);
    shape.lineTo(-TRIANGLE_HALF, TRIANGLE_HALF_HEIGHT / 3);
  } else {
    shape.moveTo(-TRIANGLE_HALF, -TRIANGLE_HALF_HEIGHT / 3);
    shape.lineTo(TRIANGLE_HALF, -TRIANGLE_HALF_HEIGHT / 3);
    shape.lineTo(0, TRIANGLE_HALF_HEIGHT * (2 / 3));
  }
  shape.closePath();
  return shape;
}
